import { useNavigate, useParams } from 'react-router-dom';

import { ATTRIBUTE_COLOURS } from '../../../../../../../../utils/buildFilterPills';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useUpdateDoc from '@/api/mutations/useUpdateDoc';
import { Doc } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { Chip } from '@/new/components/chip';
import { DocRelationships } from '@/new/components/doc-viewer/doc-relationships';
import { EditableField } from '@/new/components/editable-field';
import { Icon } from '@/new/components/icon';
import { ProcessingChip, getProcessingState } from '@/new/components/processing-chip';
import { RelativityMetadataDisplay } from '@/new/components/relativity-metadata-display';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import { Label } from '@/new/components/shadcn/label';
import { cn } from '@/new/lib/utils';

interface DocDetailsPanelProps {
  doc: Doc & { hasKeyFacts: boolean; hasRelevantFacts: boolean };
}

export const DocDetailsPanel = ({ doc }: DocDetailsPanelProps) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const navigate = useNavigate();

  const { mutate: updateDocFileName, isLoading: isUpdatingFileName } = useUpdateDoc();
  const { mutate: updateDocAuthor, isLoading: isUpdatingAuthor } = useUpdateDoc();
  const { mutate: updateDocDate, isLoading: isUpdatingDate } = useUpdateDoc();

  const handleUpdateDocFileName = (value: string) => {
    updateDocFileName({ caseId: subsetId, docId: doc.id, field: 'filename', value });
    trackCustomEvent(TrackingEventsV2.UpdateDocumentName, { documentId: doc.id });
  };

  const handleUpdateDocAuthor = (value: string) => {
    updateDocAuthor({ caseId: subsetId, docId: doc.id, field: 'author', value });
    trackCustomEvent(TrackingEventsV2.UpdateDocumentAuthor, { documentId: doc.id });
  };

  const handleUpdateDocDate = (value: string) => {
    updateDocDate({ caseId: subsetId, docId: doc.id, field: 'dateText', value });
    trackCustomEvent(TrackingEventsV2.UpdateDocumentDate, { documentId: doc.id });
  };

  const handleViewDocument = () => {
    navigate(`/matters/${matterId}/subsets/${subsetId}/docs/${doc.id}`);
    trackCustomEvent(TrackingEventsV2.ClickViewDocument, { documentId: doc.id });
  };

  const formatAuthorString = (author: string | undefined | null) => {
    if (author === undefined || author === null) {
      return '';
    }

    if (author.length < 50) {
      return author;
    }

    const authors = author.split(', ');
    let trimmedAuthorString = '';

    for (const authorItem of authors) {
      trimmedAuthorString += authorItem;

      if (trimmedAuthorString.length >= 50) {
        return `${trimmedAuthorString} ...`;
      }

      trimmedAuthorString += ', ';
    }

    return `${author.slice(0, 50)} ...`;
  };

  return (
    <div className="h-full overflow-y-auto">
      {/* Header */}
      <div className="rounded-tr-md border-b bg-gray-50 px-6 py-4">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-base font-semibold text-gray-800">
              <EditableField value={doc.file.name} onSave={handleUpdateDocFileName} />
            </h1>

            <div className="mt-2 flex items-center gap-2">
              <Chip className={cn('border', ATTRIBUTE_COLOURS.extension)}>{doc.file.extension.toUpperCase()}</Chip>

              {doc.type && <Chip className={cn('border', ATTRIBUTE_COLOURS.docType)}>{doc.type}</Chip>}

              {doc.hasKeyFacts && (
                <Chip className={cn('border', ATTRIBUTE_COLOURS.hasKeyFacts)}>
                  <Icon className="lucide--key-round" />
                  Contains Key Facts
                </Chip>
              )}

              {doc.hasRelevantFacts && (
                <Chip className={cn('border', ATTRIBUTE_COLOURS.hasRelevantFacts)}>
                  <Icon className="lucide--check" />
                  Contains Relevant Facts
                </Chip>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-6 px-6 py-6">
        {/* Summary */}
        {doc.summary && <div className="mb-6 rounded-md text-sm leading-relaxed text-gray-700">{doc.summary}</div>}

        {/* View Document Button */}
        <Button onClick={handleViewDocument} className="w-full justify-center py-2" variant="default">
          View Full Document
          <Icon className="lucide--arrow-right" />
        </Button>

        {/* Issues */}
        {doc.issues && doc.issues.length > 0 && (
          <div className="space-y-4">
            <Label className="text-xs uppercase">Issues</Label>
            <div className="my-2 flex flex-wrap gap-2">
              {doc.issues.map((issue, index) => (
                <Badge key={index} className="bg-blue-100/60 text-blue-600">
                  {issue}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Document Information */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Document Date</Label>
            <div className="text-sm text-gray-900">
              {doc.date ? (
                <EditableField value={formatDate(doc.date)} onSave={handleUpdateDocDate} />
              ) : (
                <span className="text-sm text-gray-900">N/A</span>
              )}
            </div>
          </div>

          {doc.author && (
            <div className="space-y-1">
              <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Author</Label>
              <div className="text-sm text-gray-900">
                <EditableField value={formatAuthorString(doc.author)} onSave={handleUpdateDocAuthor} />
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Facts Count</Label>

            <div className="text-sm text-gray-900">{doc.factsCount || 0}</div>
          </div>
        </div>

        {/* Processing Status */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Upload Date</Label>
            <div className="text-sm text-gray-900">{formatDate(doc.createdDate)}</div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">Status</Label>
            <div className="text-sm text-gray-900">
              <ProcessingChip processingState={getProcessingState(doc)} />
            </div>
          </div>
        </div>

        {/* Relativity Metadata */}
        {doc.file.relativityMetadata && <RelativityMetadataDisplay metadata={doc.file.relativityMetadata} />}

        {/* Document Relationships */}
        {((doc.file.parentFiles && doc.file.parentFiles.length > 0) ||
          (doc.file.childFiles && doc.file.childFiles.length > 0)) && <DocRelationships doc={doc} />}
      </div>
    </div>
  );
};
